import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Col, Row } from 'reactstrap'
import CustomSelect from '../../../common/CustomReactSelectComponent'
import { closeModal } from '../../../common/GlobalModal/redux/GlobalModalActions'
import CustomInput from '../../../common/InputGroup'
import CustomEditor from '../../../common/CustomEditor'
import DatePicker from '../../../common/ReactDatePicker/ReactDatePicker'
import { LOGISTIC_CUSTOMER_LOAD_EQUIPMENT, LOGISTIC_CUSTOMER_LOAD_TYPE } from '../../../constants/supportTypes'
import useGetStates from '../../../hooks/useGetStates'
import useGetSupportTable from '../../../hooks/useGetSupportTable'
import { clearCustomerLoad, createLoad, getCustomerLoad, updateLoad } from '../redux/loadActions'
import moment from 'moment'

import './../Loads.scss'

export default function LoadsForm({ editing, loadId, options }) {
  const [state, setState] = useState({})

  const { errors, load } = useSelector((state) => ({
    errors: state.errors,
    load: state.customerLoads.load,
  }))
  const dispatch = useDispatch()

  const stateOptions = useGetStates({ isAbbreviation: false })
  const loadTypeOptions = useGetSupportTable(LOGISTIC_CUSTOMER_LOAD_TYPE)
  const loadEquipmentOptions = useGetSupportTable(LOGISTIC_CUSTOMER_LOAD_EQUIPMENT)

  useEffect(() => {
    return () => {
      dispatch(clearCustomerLoad())
    }
  }, [])

  useEffect(() => {
    if (editing && loadId) {
      dispatch(getCustomerLoad(loadId))
    }
  }, [editing, loadId])

  useEffect(() => {
    if (editing && load) {
      const {
        originCity,
        originStateId,
        originZip,
        destinationCity,
        destinationStateId,
        destinationZip,
        pickUpDate,
        loadPay,
        loadTypeId,
        product,
        equipmentId,
        weight,
        notes,
      } = load

      setState({
        originCity,
        originStateId,
        originZip,
        destinationCity,
        destinationStateId,
        destinationZip,
        pickUpDate,
        loadPay,
        loadTypeId,
        product,
        equipmentId,
        weight,
        notes,
      })
    }
  }, [editing, load])

  const handleChange = (e) => {
    const { name, value } = e.target

    setState((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleEditorChange = (e, name) => {
    setState((prev) => ({
      ...prev,
      [name]: e.htmlValue,
    }))
  }

  const handleSelectChange = (values, e) => {
    const { name } = e

    setState((prev) => ({
      ...prev,
      [name]: values.value || values.id,
    }))
  }

  const handleDateChange = (date, name) => {
    setState((prev) => ({
      ...prev,
      [name]: date ? moment(date).format('MM/DD/YYYY hh:mm a') : null,
    }))
  }

  const handleCancelClick = () => {
    dispatch(closeModal('create_load'))
  }

  const handleSubmit = () => {
    if (editing) {
      dispatch(updateLoad(loadId, state, options))
    } else {
      dispatch(createLoad(state, options))
    }
  }

  return (
    <>
      <Row className='mt-3'>
        <Col>
          <CustomInput
            name='originCity'
            type='text'
            value={state?.originCity}
            label='Origin City'
            onChange={handleChange}
            invalid={errors?.originCity}
            errorMess={errors?.originCity}
          />
        </Col>
        <Col>
          <CustomSelect
            label='Origin State'
            onChange={handleSelectChange}
            defaultValue={state?.originStateId || null}
            name='originStateId'
            options={stateOptions || []}
            invalid={errors?.originStateId}
            errorMess={errors?.originStateId}
          />
        </Col>
        <Col>
          <CustomInput
            name='originZip'
            type='number'
            value={state?.originZip}
            label='Origin Zip'
            onChange={handleChange}
            invalid={errors?.originZip}
            errorMess={errors?.originZip}
          />
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <CustomInput
            name='destinationCity'
            type='text'
            value={state?.destinationCity}
            label='Destination City'
            onChange={handleChange}
            invalid={errors?.destinationCity}
            errorMess={errors?.destinationCity}
          />
        </Col>
        <Col>
          <CustomSelect
            label='Destination State'
            defaultValue={state?.destinationStateId || null}
            name='destinationStateId'
            options={stateOptions}
            onChange={handleSelectChange}
            invalid={errors?.destinationStateId}
            errorMess={errors?.destinationStateId}
          />
        </Col>
        <Col>
          <CustomInput
            name='destinationZip'
            type='number'
            value={state?.destinationZip}
            label='Destination Zip'
            onChange={handleChange}
            invalid={errors?.destinationZip}
            errorMess={errors?.destinationZip}
          />
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <DatePicker
            label='Pick Up Date'
            placeholder={'mm/dd/yyyy'}
            dateFormat={'MM/dd/yyyy'}
            selected={state?.pickUpDate ? new Date(state.pickUpDate) : ''}
            onChange={(date) => handleDateChange(date, 'pickUpDate')}
            borderBottomOnly
            errorMess={errors?.pickUpDate}
          />
        </Col>
        <Col>
          <CustomInput
            name='loadPay'
            type='number'
            value={state?.loadPay}
            label='Load Pay'
            onChange={handleChange}
            invalid={errors?.loadPay}
            errorMess={errors?.loadPay}
          />
        </Col>
        <Col>
          <CustomSelect
            label='Load Type'
            defaultValue={state?.loadTypeId || null}
            name='loadTypeId'
            options={loadTypeOptions || []}
            onChange={handleSelectChange}
            invalid={errors?.loadTypeId}
            errorMess={errors?.loadTypeId}
          />
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <CustomInput
            name='product'
            type='text'
            value={state?.product}
            label='Product'
            onChange={handleChange}
            invalid={errors?.product}
            errorMess={errors?.product}
          />
        </Col>
        <Col>
          <CustomSelect
            label='Equipment'
            defaultValue={state?.equipmentId || null}
            name='equipmentId'
            options={loadEquipmentOptions || []}
            onChange={handleSelectChange}
            invalid={errors?.equipmentId}
            errorMess={errors?.equipmentId}
          />
        </Col>
        <Col>
          <CustomInput
            name='weight'
            type='number'
            value={state?.weight}
            label='Weight'
            onChange={handleChange}
            invalid={errors?.weight}
            errorMess={errors?.weight}
          />
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <label htmlFor='notes'>Notes</label>
          <CustomEditor
            name='notes'
            onChange={(e) => handleEditorChange(e, 'notes')}
            value={state?.notes}
            height={'250px'}
            customStyle={{ backgroundColor: 'none' }}
          />
        </Col>
      </Row>
      <Row>
        <Col className='loads__footer'>
          <Button className='mr-1 btn-gold' onClick={handleSubmit}>
            OK
          </Button>
          <Button onClick={handleCancelClick}>Cancel</Button>
        </Col>
      </Row>
    </>
  )
}
